export const teacherDashboardMockData = {
    headline: {
        title: 'Teacher Home',
        subtitle: 'Your next moves across active courses.',
        focus: 'Keep projects moving, stay on grade cadence, and reduce tomorrow-morning decisions.',
    },

    operatingBrief: {
        primaryCourseId: 'engineering',
        label: 'Your next step',
        safeUseLabel: 'Teacher-facing support only - no student-sensitive data required',
    },

    homeHero: {
        readyMessage: '{primaryClass} is ready for tomorrow.',
        label: 'Your next step',
        title: 'Generate tomorrow\'s lesson',
        explanation:
            'Based on pacing, recent classes, and student progress, Saige has prepared the next lesson for you.',
        contextChips: [
            'Students improving',
        ],
    },

    todaySchedule: [
        {
            id: 'engineering-morning',
            time: '8:15 AM',
            label: 'Engineering',
            detail: 'Room 204',
        },
        {
            id: 'planning-period',
            time: '9:45 AM',
            label: 'Planning Period',
            detail: 'Room 204',
        },
        {
            id: 'computer-science-late-morning',
            time: '11:15 AM',
            label: 'Computer Science',
            detail: 'Room 204',
        },
        {
            id: 'office-hours',
            time: '1:00 PM',
            label: 'Office Hours',
            detail: 'Room 204',
        },
    ],

    classroomInsights: [
        {
            id: 'on-track',
            label: 'Students on track',
            value: '18 of 25',
            detail: 'students are on track for the current unit.',
            tone: 'teal',
        },
        {
            id: 'support',
            label: 'Students need support',
            value: '3',
            detail: 'students may benefit from intervention this week.',
            tone: 'amber',
        },
        {
            id: 'progress',
            label: 'Great progress',
            value: '+8%',
            detail: 'class average is up from last week.',
            tone: 'emerald',
        },
    ],

    upcoming: [
        {
            id: 'engineering-quiz',
            day: 'Thu',
            label: 'Safety Retest Check',
            detail: 'Engineering',
        },
        {
            id: 'cs-exit-ticket',
            day: 'Fri',
            label: 'CodeHS Exit Ticket',
            detail: 'Computer Science',
        },
        {
            id: 'engineering-checkpoint',
            day: 'Mon',
            label: 'Dragster Build Checkpoint',
            detail: 'Engineering',
        },
    ],

    watchAheadNote: {
        text: 'Saige is watching ahead: Next week is a short week. Review pacing before generating next week\'s lessons.',
    },

    weeklyPriorities: [
        {
            id: 'course-blocker-clearance',
            label: 'Clear the highest-priority course blocker',
            detail: 'Use the course cards below to act on the class with the most urgent pacing, safety, or grading need.',
            urgency: 'high',
        },
        {
            id: 'grade-window-checkpoint',
            label: 'Choose the next grade opportunity',
            detail: 'Convert real classroom work into the next minor or major grade before the window gets tight.',
            urgency: 'high',
        },
        {
            id: 'weekly-student-deliverable',
            label: 'Make this week\'s student deliverable explicit',
            detail: 'Name what students should produce, how it will be checked, and what Saige should prepare.',
            urgency: 'medium',
        },
    ],

    aiPreparationQueue: [
        {
            id: 'engineering-dragster-build-day',
            course: 'Engineering',
            label: 'Dragster build-day packet',
            output: 'Lesson flow, station checklist, cleanup routine, and checkpoint rubric',
            reason: 'Prepares the next hands-on shop phase while keeping safety and grading visible.',
            actionLabel: 'Prepare Engineering packet',
            safetyLabel: 'Teacher draft - no student-sensitive data',
        },
        {
            id: 'cs-codehs-mini-project',
            course: 'Computer Science',
            label: 'CodeHS mini-project brief',
            output: 'Student-facing task, debugging reflection, and minor-grade rubric',
            reason: 'Turns platform progress into a visible project deliverable instead of isolated screen work.',
            actionLabel: 'Prepare CS project brief',
            safetyLabel: 'Teacher draft - verify district expectations',
        },
        {
            id: 'grade-cadence-check',
            course: 'All active courses',
            label: 'Grade cadence opportunity list',
            output: 'Real classroom activities that can become the next minor or major grade',
            reason: 'Reduces last-minute grade-window pressure without inventing artificial assignments.',
            actionLabel: 'Open grade workflow',
            safetyLabel: 'Teacher-facing support only',
        },
    ],

    courses: [
        {
            id: 'engineering',
            name: 'Engineering',
            shortName: 'ENG',
            currentUnit: 'Dragster Car Project',
            status: 'Ready for build phase',
            urgency: 'high',
            priorityReason:
                'Safety retests and shop readiness are blocking the next build phase.',
            bestNextMove:
                'Run safety retests, confirm shop readiness, then begin dragster block cutting with a clear checkpoint grade.',
            whyItMatters:
                'Students are near the hands-on build phase. A clear safety and grading workflow prevents chaos once tools and materials are active.',
            nextActions: [
                'Print or display safety retest list.',
                'Prepare dragster cutting checklist.',
                'Create a minor grade for design sketch or build-readiness checkpoint.',
            ],
            prepActions: [
                {
                    id: 'engineering-generate-tomorrow-lesson',
                    label: 'Generate tomorrow\'s lesson',
                    detail: 'Build the dragster block-cutting flow from the current checkpoint.',
                    type: 'lesson',
                    tone: 'primary',
                },
                {
                    id: 'engineering-open-grade-workflow',
                    label: 'Open grade workflow',
                    detail: 'Use the safety retest or design sketch as this week\'s minor grade.',
                    type: 'grade',
                    tone: 'secondary',
                },
                {
                    id: 'engineering-review-resources',
                    label: 'Review resources',
                    detail: 'Check safety retest list, station expectations, and material queue.',
                    type: 'resources',
                    tone: 'secondary',
                },
                {
                    id: 'engineering-note-pacing',
                    label: 'Note pacing concern',
                    detail: 'Safety retests and shop readiness are blocking the build phase.',
                    type: 'pacing',
                    tone: 'secondary',
                },
            ],
            projectCheckpoint: {
                label: 'Safety-cleared dragster build launch',
                dueWindow: 'This week',
                studentDeliverable: 'Passed safety retest or build-readiness sketch',
                gradeOpportunity: 'Minor grade: design sketch or shop-readiness checkpoint',
                teacherPrep: 'Station flow, cleanup expectations, and material queue',
                safetyLabel: 'No student-sensitive data required',
            },
            teacherPrepChecklist: [
                'Confirm safety retest process before tool access.',
                'Post cutting station expectations and cleanup roles.',
                'Choose the checkpoint that becomes this week\'s minor grade.',
            ],
            gradeCadence: {
                windowLabel: 'Current 3-week window',
                minorCompleted: 2,
                minorRequired: 3,
                majorCompleted: 1,
                majorRequired: 1,
            },
            suggestedSaigeAction: 'Generate dragster build day lesson',
        },
        {
            id: 'computer-science',
            name: 'Computer Science',
            shortName: 'CS',
            currentUnit: 'CodeHS + Applied Practice',
            status: 'Needs stronger project structure',
            urgency: 'medium',
            priorityReason:
                'Project structure and grade cadence need attention before the next checkpoint.',
            bestNextMove:
                'Turn the next CodeHS concept into a small applied coding deliverable and add a short touch-typing routine.',
            whyItMatters:
                'CodeHS should support the class, not become the whole experience. A project deliverable makes progress visible and gradeable.',
            nextActions: [
                'Create a mini project brief.',
                'Add a 5-8 minute touch-typing routine.',
                'Identify the next minor grade checkpoint.',
            ],
            prepActions: [
                {
                    id: 'cs-generate-tomorrow-lesson',
                    label: 'Generate tomorrow\'s lesson',
                    detail: 'Turn the current CodeHS concept into a short applied project block.',
                    type: 'lesson',
                    tone: 'primary',
                },
                {
                    id: 'cs-open-grade-workflow',
                    label: 'Open grade workflow',
                    detail: 'Set the applied coding deliverable as the next minor grade checkpoint.',
                    type: 'grade',
                    tone: 'secondary',
                },
                {
                    id: 'cs-prepare-materials',
                    label: 'Prepare class materials',
                    detail: 'Post project evidence requirements and the touch-typing routine.',
                    type: 'materials',
                    tone: 'secondary',
                },
                {
                    id: 'cs-note-pacing',
                    label: 'Note pacing concern',
                    detail: 'CodeHS needs a visible project structure before the next checkpoint.',
                    type: 'pacing',
                    tone: 'secondary',
                },
            ],
            projectCheckpoint: {
                label: 'Applied CodeHS mini-project',
                dueWindow: 'Next class checkpoint',
                studentDeliverable: 'Small program, debugging reflection, and submission link',
                gradeOpportunity: 'Minor grade: applied coding deliverable',
                teacherPrep: 'Pick the CodeHS concept and define acceptable evidence',
                safetyLabel: 'No student-sensitive data required',
            },
            teacherPrepChecklist: [
                'Select one CodeHS skill to turn into a project task.',
                'Post a touch-typing routine that emphasizes home-row form.',
                'Create one submission checkpoint before the next grade review.',
            ],
            gradeCadence: {
                windowLabel: 'Current 3-week window',
                minorCompleted: 1,
                minorRequired: 3,
                majorCompleted: 0,
                majorRequired: 1,
            },
            suggestedSaigeAction: 'Generate CS mini-project brief',
        },
    ],
};

export const teacherDashboardMockEvents = {
    1: [
        {
            time: '8:15 AM',
            course: 'Engineering',
            topic: 'Safety retest and tool-access readiness',
            type: 'Safety',
            gradeOpportunity: 'Minor grade: safety retest or build-readiness sketch',
            teacherPrep: 'Print retest list and post tool-access expectations.',
            safetyLabel: 'Teacher-facing support only',
        },
        {
            time: '10:05 AM',
            course: 'Computer Science',
            topic: 'Touch-typing routine and CodeHS skill check',
            type: 'Skill routine',
            gradeOpportunity: 'Practice evidence for next checkpoint',
            teacherPrep: 'Post 5-8 minute home-row routine.',
            safetyLabel: 'No student-sensitive data required',
        },
        {
            time: '2:10 PM',
            course: 'Planning',
            topic: 'Choose this week\'s grade opportunity',
            type: 'Prep',
            gradeOpportunity: 'Confirm one minor grade for each active course',
            teacherPrep: 'Map real classwork to the grade window.',
            safetyLabel: 'Teacher-facing support only',
        },
    ],
    2: [
        {
            time: '8:15 AM',
            course: 'Engineering',
            topic: 'Dragster block cutting station launch',
            type: 'Project build',
            gradeOpportunity: 'Minor grade: station-readiness checkpoint',
            teacherPrep: 'Confirm station flow, material queue, and cleanup roles.',
            safetyLabel: 'No student-sensitive data required',
        },
        {
            time: '10:05 AM',
            course: 'Computer Science',
            topic: 'CodeHS concept to mini-project brief',
            type: 'Project design',
            gradeOpportunity: 'Minor grade: applied coding deliverable',
            teacherPrep: 'Pick the current CodeHS concept and define acceptable evidence.',
            safetyLabel: 'Teacher-facing support only',
        },
        {
            time: '2:10 PM',
            course: 'Planning',
            topic: 'Prepare dragster build day lesson',
            type: 'Prep',
            gradeOpportunity: 'Use build launch as a checkpoint grade',
            teacherPrep: 'Generate or refine the build-day lesson plan.',
            safetyLabel: 'Teacher-facing support only',
        },
    ],
    3: [
        {
            time: '8:15 AM',
            course: 'Engineering',
            topic: 'Dragster design sketch review',
            type: 'Checkpoint',
            gradeOpportunity: 'Minor grade: design sketch review',
            teacherPrep: 'Set the review table and note common redesign needs.',
            safetyLabel: 'No student-sensitive data required',
        },
        {
            time: '10:05 AM',
            course: 'Computer Science',
            topic: 'Applied mini-project work block',
            type: 'Project work',
            gradeOpportunity: 'Collect submission link and debugging reflection',
            teacherPrep: 'Post deliverable requirements before work time.',
            safetyLabel: 'Teacher-facing support only',
        },
        {
            time: '12:45 PM',
            course: 'PLC',
            topic: 'Pacing and grade cadence check',
            type: 'Compliance support',
            gradeOpportunity: 'Confirm remaining minor and major grade needs',
            teacherPrep: 'Bring course grade counts and pacing blockers.',
            safetyLabel: 'Teacher-facing support only',
        },
    ],
    4: [
        {
            time: '8:15 AM',
            course: 'Engineering',
            topic: 'Shop cleanup routine and milestone check',
            type: 'Safety routine',
            gradeOpportunity: 'Completion evidence for build-readiness checkpoint',
            teacherPrep: 'Assign cleanup roles and verify tool return routine.',
            safetyLabel: 'No student-sensitive data required',
        },
        {
            time: '10:05 AM',
            course: 'Computer Science',
            topic: 'Debugging reflection and code cleanup',
            type: 'Reflection',
            gradeOpportunity: 'Minor grade: debugging reflection',
            teacherPrep: 'Prepare reflection prompts tied to the mini-project.',
            safetyLabel: 'Teacher-facing support only',
        },
        {
            time: '1:20 PM',
            course: 'Intervention',
            topic: 'Catch up missing checkpoint evidence',
            type: 'Student support',
            gradeOpportunity: 'Recover missing minor-grade evidence',
            teacherPrep: 'List missing non-sensitive deliverables by course.',
            safetyLabel: 'Do not store student-sensitive data',
        },
    ],
    5: [
        {
            time: '8:15 AM',
            course: 'Engineering',
            topic: 'Dragster milestone evidence review',
            type: 'Checkpoint',
            gradeOpportunity: 'Minor grade: build-readiness evidence',
            teacherPrep: 'Record which deliverables are ready for review.',
            safetyLabel: 'No student-sensitive data required',
        },
        {
            time: '10:05 AM',
            course: 'Computer Science',
            topic: 'Mini-project submission checkpoint',
            type: 'Checkpoint',
            gradeOpportunity: 'Minor grade: applied coding deliverable',
            teacherPrep: 'Confirm submission format and reflection criteria.',
            safetyLabel: 'Teacher-facing support only',
        },
        {
            time: '2:10 PM',
            course: 'Planning',
            topic: 'Next week pacing and grade window setup',
            type: 'Prep',
            gradeOpportunity: 'Identify next major-grade candidate',
            teacherPrep: 'Choose next week\'s project milestone and assessment window.',
            safetyLabel: 'Teacher-facing support only',
        },
    ],
};
